import {useEffect, useState} from 'react';

import {GetNeighborhoodsDentalGuideParams, TypeLocalNameEnum} from 'domain/usecases';

import {capitalize} from '@mui/material';
import {OdontoGuideServicesTypeEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {parseCommaSeparated} from 'presentation/modules/booking/shared/utils/parseCommaSeparated';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useGetOdontoDistrictsQuery} from '../../queries/useGetOdontoDistrictsQuery';
import {UseDistrictSelectionProps} from './types';
import {textHandle} from 'presentation/utils/textHandle';

const {all, selectAll} = translations['pt-br'].commons;

export function useOdontoDistrictSelection({handleNextStep}: UseDistrictSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const {isMobile} = useMediaQueries();

	const {data: healthCard, isFetching: isFetchingHealthCard} = useGetHealthCardQuery();

	const cdPlanoANS =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns || '';

	const districtParams: GetNeighborhoodsDentalGuideParams = {
		cdEspecialidade: String(bookingData.specialtyCode),
		nmCidade: bookingData.cityName,
		cdUf: bookingData.ufCode,
		cdPlanoANS,
		dsServico: OdontoGuideServicesTypeEnum.OUTPATIENT,
		tpLocalidade: capitalize(TypeLocalNameEnum.CIDADE.toLowerCase()),
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetOdontoDistrictsQuery({params: districtParams});

	const isLoading = districtListIsLoading || isFetchingHealthCard;

	const districtListHasUnprocessableError = isUnprocessableError(districtListError);

	const showNotFoundState =
		!districtListIsLoading &&
		(!districtList || districtList.length === 0 || districtListHasUnprocessableError);

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			let updatedSelected: string[];

			if (prevSelected.includes(district)) {
				updatedSelected = prevSelected.filter(item => item !== district);
			} else {
				updatedSelected = [...prevSelected, district];
			}

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const sanitizedQuery = textHandle(query).sanitizeText();

	const filteredDistrictList =
		districtList?.filter(districtItem => {
			const sanitizedDistrict = textHandle(districtItem.district).sanitizeText();
			return sanitizedDistrict.includes(sanitizedQuery);
		}) ?? [];

	const showEmptyState = filteredDistrictList?.length === 0;

	useEffect(() => {
		setHideSearchBarState(showNotFoundState);
	}, [setHideSearchBarState, showNotFoundState]);

	return {
		isLoading,
		districtListHasError,
		districtListHasUnprocessableError,
		refetch,
		showNotFoundState,
		showEmptyState,
		filteredDistrictList,
		handleSelectCard,
		selectedDistricts,
		handleGoForward,
		showEmptyDistrictsModal,
		setShowEmptyDistrictsModal,
	};
}
